/**
 * TV Display Mode — fullscreen landscape leaderboard for gym TVs.
 *
 * Features:
 * - Auto-refresh every 60s
 * - Rotates between: leaderboard, recent PRs, challenge standings, recent activity
 * - Clean fullscreen layout optimized for landscape TVs
 * - No user interaction needed
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ActivityIndicator,
  Animated,
  Dimensions,
  StatusBar,
} from 'react-native';
import { useLocalSearchParams } from 'expo-router';
import { supabase } from '@/lib/supabase';
import { fetchTVModeData, type TVModeData } from '@/lib/gymOwnerDashboard';

const { width: SW, height: SH } = Dimensions.get('window');
const s = (base: number) => Math.round(base * Math.min(SW / 1920, SH / 1080));

const C = {
  bg: '#08090E',
  headerBg: '#0D1117',
  rowOdd: '#0F1319',
  rowEven: '#131720',
  rowTop3: '#111A2B',
  border: '#1E293B',
  white: '#FFFFFF',
  dim: '#64748B',
  muted: '#475569',
  gold: '#F59E0B',
  silver: '#94A3B8',
  bronze: '#CD7F32',
  blue: '#3B82F6',
  green: '#22C55E',
  red: '#EF4444',
};

type SlideType = 'leaderboard' | 'prs' | 'challenges' | 'activity';

const SLIDE_DURATION = 15000; // 15s per slide

const ROWS_PER_PAGE = 10;

export default function TvLeaderboardScreen() {
  const { gymId } = useLocalSearchParams();
  const [data, setData] = useState<TVModeData | null>(null);
  const [loading, setLoading] = useState(true);
  const [time, setTime] = useState(new Date());
  const [fadeAnim] = useState(new Animated.Value(1));
  const [currentSlide, setCurrentSlide] = useState<SlideType>('leaderboard');
  const slideRef = useRef<SlideType>('leaderboard');
  const [lbPage, setLbPage] = useState(0);

  const load = useCallback(async () => {
    if (!gymId || typeof gymId !== 'string') return;

    Animated.timing(fadeAnim, { toValue: 0.6, duration: 200, useNativeDriver: true }).start();

    const tvData = await fetchTVModeData(gymId);
    setData(tvData);
    setLoading(false);

    Animated.timing(fadeAnim, { toValue: 1, duration: 300, useNativeDriver: true }).start();
  }, [gymId, fadeAnim]);

  // Load data and auto-refresh
  useEffect(() => {
    load();
    const interval = setInterval(load, 60000);
    return () => clearInterval(interval);
  }, [load]);

  // Clock
  useEffect(() => {
    const interval = setInterval(() => setTime(new Date()), 30000);
    return () => clearInterval(interval);
  }, []);

  // Slide rotation
  useEffect(() => {
    const slides: SlideType[] = ['leaderboard'];
    if (data?.recentPRs && data.recentPRs.length > 0) slides.push('prs');
    if (data?.activeChallenges && data.activeChallenges.length > 0) slides.push('challenges');
    if (data?.recentActivity && data.recentActivity.length > 0) slides.push('activity');

    if (slides.length <= 1) return;

    let idx = 0;
    const interval = setInterval(() => {
      idx = (idx + 1) % slides.length;
      slideRef.current = slides[idx];

      Animated.sequence([
        Animated.timing(fadeAnim, { toValue: 0, duration: 300, useNativeDriver: true }),
        Animated.timing(fadeAnim, { toValue: 1, duration: 300, useNativeDriver: true }),
      ]).start();

      // Set slide in the gap between animations
      setTimeout(() => {
        setCurrentSlide(slides[idx]);
        if (slides[idx] === 'leaderboard') setLbPage(0);
      }, 300);
    }, SLIDE_DURATION);

    return () => clearInterval(interval);
  }, [data, fadeAnim]);

  // Leaderboard page auto-advance
  useEffect(() => {
    if (currentSlide !== 'leaderboard' || !data?.leaderboard?.length) return;
    const total = Math.ceil(data.leaderboard.length / ROWS_PER_PAGE);
    if (total <= 1) return;

    const interval = setInterval(() => {
      setLbPage(prev => (prev + 1) % total);
      Animated.sequence([
        Animated.timing(fadeAnim, { toValue: 0.3, duration: 200, useNativeDriver: true }),
        Animated.timing(fadeAnim, { toValue: 1, duration: 300, useNativeDriver: true }),
      ]).start();
    }, 8000);

    return () => clearInterval(interval);
  }, [currentSlide, data?.leaderboard?.length, fadeAnim]);

  const rankColor = (r: number) => r === 1 ? C.gold : r === 2 ? C.silver : r === 3 ? C.bronze : C.dim;
  const rankBg = (r: number, i: number) => r <= 3 ? C.rowTop3 : i % 2 === 0 ? C.rowOdd : C.rowEven;

  const timeStr = time.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  const dateStr = time.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });

  if (loading && !data) {
    return (
      <View style={st.root}>
        <StatusBar hidden />
        <ActivityIndicator size="large" color={C.blue} />
      </View>
    );
  }

  const allLbRows = data?.leaderboard || [];
  const totalLbPages = Math.ceil(allLbRows.length / ROWS_PER_PAGE);
  const pagedLbRows = allLbRows.slice(lbPage * ROWS_PER_PAGE, (lbPage + 1) * ROWS_PER_PAGE);
  const gymName = data?.gymName || 'GYM LEADERBOARD';

  // Use gym branding if available, fall back to DUEL defaults
  const accentColor = data?.branding?.accent_color ?? C.gold;
  const logoUrl = data?.branding?.logo_url ?? null;

  const slideLabel = currentSlide === 'leaderboard' ? 'STRENGTH LEADERBOARD'
    : currentSlide === 'prs' ? 'RECENT PRs'
    : currentSlide === 'challenges' ? 'ACTIVE CHALLENGES'
    : 'RECENT ACTIVITY';

  return (
    <View style={st.root}>
      <StatusBar hidden />

      {/* ── HEADER ── */}
      <View style={st.header}>
        <View style={st.headerLeft}>
          {logoUrl ? (
            // eslint-disable-next-line @typescript-eslint/no-require-imports
            <View style={[st.gymLogoWrap, { borderColor: accentColor }]}>
              {/* @ts-ignore – Image import kept local to avoid circular deps */}
              {/* Rendered by the platform Image; import inline to avoid top-level import churn */}
              {React.createElement(require('react-native').Image, {
                source: { uri: logoUrl },
                style: { width: s(40), height: s(40), resizeMode: 'contain' },
              })}
            </View>
          ) : (
            <Text style={[st.logo, { color: accentColor }]}>DUEL</Text>
          )}
          <View style={st.logoDivider} />
          <Text style={st.headerLabel}>{slideLabel}</Text>
        </View>
        <View style={st.headerRight}>
          <Text style={st.headerTime}>{timeStr}</Text>
          <Text style={st.headerDate}>{dateStr}</Text>
        </View>
      </View>

      {/* ── GYM NAME BAR ── */}
      <View style={st.gymBar}>
        <View style={[st.gymAccent, { backgroundColor: accentColor }]} />
        <Text style={st.gymName}>{gymName.toUpperCase()}</Text>
        <View style={[st.gymAccent, { backgroundColor: accentColor }]} />
      </View>

      {/* ── CONTENT ── */}
      <Animated.View style={[st.list, { opacity: fadeAnim }]}>
        {currentSlide === 'leaderboard' && (
          <>
            {totalLbPages > 1 && (
              <View style={{ flexDirection: 'row', justifyContent: 'center', paddingVertical: s(8), gap: s(8) }}>
                <Text style={{ color: C.muted, fontSize: s(14), fontFamily: 'Inter_700Bold', letterSpacing: 2 }}>
                  {lbPage + 1} / {totalLbPages}
                </Text>
              </View>
            )}
            {renderLeaderboard(pagedLbRows)}
          </>
        )}
        {currentSlide === 'prs' && renderPRs(data?.recentPRs || [])}
        {currentSlide === 'challenges' && renderChallenges(data?.activeChallenges || [])}
        {currentSlide === 'activity' && renderActivity(data?.recentActivity || [])}
      </Animated.View>

      {/* ── FOOTER ── */}
      <View style={st.footer}>
        <Text style={st.footerText}>POWERED BY DUEL</Text>
        <Text style={st.footerDot}>•</Text>
        <Text style={st.footerText}>DOUEVENLIFT.COM</Text>
        <Text style={st.footerDot}>•</Text>
        <Text style={st.footerText}>LIVE • AUTO-UPDATES EVERY 60S</Text>
        <Text style={st.footerDot}>•</Text>
        {/* Slide indicator */}
        <View style={{ flexDirection: 'row', gap: s(6), alignItems: 'center' }}>
          {['leaderboard', 'prs', 'challenges', 'activity'].map(slide => (
            <View
              key={slide}
              style={{
                width: s(8), height: s(8), borderRadius: s(4),
                backgroundColor: currentSlide === slide ? C.blue : C.border,
              }}
            />
          ))}
        </View>
      </View>
    </View>
  );
}

/* ── Slide renderers ─────────────────────────────────────── */

function renderLeaderboard(rows: TVModeData['leaderboard']) {
  if (rows.length === 0) {
    return (
      <View style={st.empty}>
        <Text style={st.emptyTitle}>NO ATHLETES RANKED YET</Text>
        <Text style={st.emptySub}>Join at douevenlift.com</Text>
      </View>
    );
  }

  return (
    <>
      {/* Column headers */}
      <View style={st.colHeaders}>
        <Text style={[st.colText, { width: s(100) }]}>RANK</Text>
        <Text style={[st.colText, { flex: 1 }]}>ATHLETE</Text>
        <Text style={[st.colText, { width: s(220), textAlign: 'right' }]}>STRENGTH SCORE</Text>
      </View>

      {rows.map((row, i) => {
        const rank = row.rank;
        const rankClr = rank === 1 ? C.gold : rank === 2 ? C.silver : rank === 3 ? C.bronze : C.dim;
        const bg = rank <= 3 ? C.rowTop3 : i % 2 === 0 ? C.rowOdd : C.rowEven;

        return (
          <View key={`${row.handle}-${i}`} style={[st.row, { backgroundColor: bg }]}>
            <View style={[st.rankPip, { backgroundColor: rankClr }]} />
            <View style={st.rankCol}>
              <Text style={[st.rankNum, { color: rankClr }]}>{rank}</Text>
            </View>
            <View style={st.nameCol}>
              <Text style={[st.nameText, rank <= 3 && { color: C.white }]} numberOfLines={1}>
                {row.name}
              </Text>
              {row.handle ? <Text style={st.handleText} numberOfLines={1}>{row.handle}</Text> : null}
            </View>
            <View style={st.scoreCol}>
              <Text style={[st.scoreNum, rank === 1 && { color: C.gold }]}>
                {row.score.toLocaleString()}
              </Text>
              <Text style={st.scoreUnit}>KG</Text>
            </View>
            {rank === 1 && <View style={st.rank1Bar} />}
          </View>
        );
      })}
    </>
  );
}

function renderPRs(prs: TVModeData['recentPRs']) {
  if (prs.length === 0) {
    return (
      <View style={st.empty}>
        <Text style={st.emptyTitle}>NO RECENT PRs</Text>
      </View>
    );
  }

  return (
    <>
      <View style={st.colHeaders}>
        <Text style={[st.colText, { flex: 1 }]}>ATHLETE</Text>
        <Text style={[st.colText, { flex: 1 }]}>EXERCISE</Text>
        <Text style={[st.colText, { width: s(160), textAlign: 'right' }]}>WEIGHT</Text>
      </View>
      {prs.map((pr, i) => (
        <View key={`pr-${i}`} style={[st.row, { backgroundColor: i % 2 === 0 ? C.rowOdd : C.rowEven }]}>
          <View style={[st.rankPip, { backgroundColor: C.green }]} />
          <View style={{ flex: 1, marginLeft: s(16) }}>
            <Text style={[st.nameText, { fontSize: s(22) }]} numberOfLines={1}>{pr.name}</Text>
            {pr.handle ? <Text style={st.handleText}>{pr.handle}</Text> : null}
          </View>
          <View style={{ flex: 1 }}>
            <Text style={{ color: C.white, fontSize: s(20), fontFamily: 'Inter_600SemiBold' }} numberOfLines={1}>{pr.exercise}</Text>
          </View>
          <View style={st.scoreCol}>
            <Text style={[st.scoreNum, { color: C.green, fontSize: s(26) }]}>{pr.weight}</Text>
            <Text style={st.scoreUnit}>KG</Text>
          </View>
        </View>
      ))}
    </>
  );
}

function renderChallenges(challenges: TVModeData['activeChallenges']) {
  if (challenges.length === 0) {
    return (
      <View style={st.empty}>
        <Text style={st.emptyTitle}>NO ACTIVE CHALLENGES</Text>
      </View>
    );
  }

  return (
    <>
      {challenges.map((ch, i) => {
        const daysLeft = Math.max(0, Math.ceil((new Date(ch.end_date).getTime() - Date.now()) / 86400000));
        return (
          <View key={`ch-${i}`} style={[st.challengeCard, i === 0 && { borderColor: C.gold + '44' }]}>
            <Text style={st.challengeTitle}>{ch.title}</Text>
            <View style={{ flexDirection: 'row', gap: s(30), marginTop: s(10) }}>
              <View>
                <Text style={st.challengeStat}>{ch.participant_count}</Text>
                <Text style={st.challengeStatLabel}>PARTICIPANTS</Text>
              </View>
              <View>
                <Text style={[st.challengeStat, { color: daysLeft <= 3 ? C.red : C.dim }]}>{daysLeft}</Text>
                <Text style={st.challengeStatLabel}>DAYS LEFT</Text>
              </View>
            </View>
          </View>
        );
      })}
    </>
  );
}

function renderActivity(activity: TVModeData['recentActivity']) {
  if (activity.length === 0) {
    return (
      <View style={st.empty}>
        <Text style={st.emptyTitle}>NO RECENT ACTIVITY</Text>
      </View>
    );
  }

  return (
    <>
      <View style={st.colHeaders}>
        <Text style={[st.colText, { flex: 1 }]}>ATHLETE</Text>
        <Text style={[st.colText, { flex: 1 }]}>ACTIVITY</Text>
        <Text style={[st.colText, { width: s(180), textAlign: 'right' }]}>TIME</Text>
      </View>
      {activity.map((a, i) => {
        const t = new Date(a.time);
        const ago = Math.floor((Date.now() - t.getTime()) / 3600000);
        const timeLabel = ago < 1 ? 'Just now' : ago < 24 ? `${ago}h ago` : `${Math.floor(ago / 24)}d ago`;

        return (
          <View key={`act-${i}`} style={[st.row, { backgroundColor: i % 2 === 0 ? C.rowOdd : C.rowEven }]}>
            <View style={[st.rankPip, { backgroundColor: C.blue }]} />
            <View style={{ flex: 1, marginLeft: s(16) }}>
              <Text style={[st.nameText, { fontSize: s(22) }]} numberOfLines={1}>{a.name}</Text>
              {a.handle ? <Text style={st.handleText}>{a.handle}</Text> : null}
            </View>
            <View style={{ flex: 1 }}>
              <Text style={{ color: C.silver, fontSize: s(18), fontFamily: 'Inter_500Medium' }}>{a.action}</Text>
            </View>
            <View style={{ width: s(180), alignItems: 'flex-end' }}>
              <Text style={{ color: C.dim, fontSize: s(18), fontFamily: 'Inter_600SemiBold' }}>{timeLabel}</Text>
            </View>
          </View>
        );
      })}
    </>
  );
}

/* ── Styles ──────────────────────────────────────────────── */

const st = StyleSheet.create({
  root: { flex: 1, backgroundColor: C.bg, justifyContent: 'center' },

  header: {
    flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center',
    paddingHorizontal: s(40), paddingTop: s(24), paddingBottom: s(16),
    backgroundColor: C.headerBg, borderBottomWidth: 1, borderBottomColor: C.border,
  },
  headerLeft: { flexDirection: 'row', alignItems: 'center', gap: s(16) },
  logo: { color: C.white, fontSize: s(36), fontFamily: 'Inter_700Bold', letterSpacing: 4 },
  gymLogoWrap: { width: s(48), height: s(48), borderRadius: s(8), borderWidth: 2, alignItems: 'center', justifyContent: 'center', overflow: 'hidden' },
  logoDivider: { width: 2, height: s(28), backgroundColor: C.border },
  headerLabel: { color: C.dim, fontSize: s(16), fontFamily: 'Inter_700Bold', letterSpacing: 3 },
  headerRight: { alignItems: 'flex-end' },
  headerTime: { color: C.white, fontSize: s(24), fontFamily: 'Inter_700Bold' },
  headerDate: { color: C.dim, fontSize: s(14), fontFamily: 'Inter_500Medium', marginTop: 2 },

  gymBar: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', paddingVertical: s(20), gap: s(20) },
  gymAccent: { height: 2, flex: 1, backgroundColor: C.border },
  gymName: { color: C.white, fontSize: s(32), fontFamily: 'Inter_700Bold', letterSpacing: 6 },

  colHeaders: { flexDirection: 'row', paddingHorizontal: s(40), paddingVertical: s(10), borderBottomWidth: 1, borderBottomColor: C.border },
  colText: { color: C.muted, fontSize: s(13), fontFamily: 'Inter_700Bold', letterSpacing: 2 },

  list: { flex: 1, paddingHorizontal: s(32), paddingTop: s(8) },

  row: {
    flexDirection: 'row', alignItems: 'center', paddingVertical: s(14), paddingHorizontal: s(12),
    marginBottom: s(4), borderRadius: 4, position: 'relative', overflow: 'hidden',
  },
  rankPip: { width: 4, height: '70%', borderRadius: 2, position: 'absolute', left: 0 },
  rankCol: { width: s(80), alignItems: 'center' },
  rankNum: { fontSize: s(28), fontFamily: 'Inter_700Bold' },
  nameCol: { flex: 1, marginLeft: s(12) },
  nameText: { color: C.silver, fontSize: s(24), fontFamily: 'Inter_700Bold' },
  handleText: { color: C.muted, fontSize: s(15), fontFamily: 'Inter_500Medium', marginTop: 1 },
  scoreCol: { flexDirection: 'row', alignItems: 'baseline', gap: s(6), minWidth: s(180), justifyContent: 'flex-end' },
  scoreNum: { color: C.white, fontSize: s(30), fontFamily: 'Inter_700Bold' },
  scoreUnit: { color: C.muted, fontSize: s(14), fontFamily: 'Inter_700Bold', letterSpacing: 1 },
  rank1Bar: { position: 'absolute', bottom: 0, left: 0, right: 0, height: 2, backgroundColor: C.gold },

  empty: { flex: 1, justifyContent: 'center', alignItems: 'center' },
  emptyTitle: { color: C.white, fontSize: s(32), fontFamily: 'Inter_700Bold', letterSpacing: 3 },
  emptySub: { color: C.blue, fontSize: s(22), fontFamily: 'Inter_600SemiBold', marginTop: s(12) },

  // Challenges slide
  challengeCard: {
    backgroundColor: C.rowTop3, borderRadius: 4, borderWidth: 1, borderColor: C.border,
    padding: s(24), marginBottom: s(12),
  },
  challengeTitle: { color: C.white, fontSize: s(28), fontFamily: 'Inter_700Bold' },
  challengeStat: { color: C.white, fontSize: s(36), fontFamily: 'Inter_700Bold' },
  challengeStatLabel: { color: C.muted, fontSize: s(13), fontFamily: 'Inter_700Bold', letterSpacing: 2 },

  footer: {
    flexDirection: 'row', justifyContent: 'center', alignItems: 'center',
    paddingVertical: s(14), backgroundColor: C.headerBg,
    borderTopWidth: 1, borderTopColor: C.border, gap: s(12),
  },
  footerText: { color: C.muted, fontSize: s(13), fontFamily: 'Inter_700Bold', letterSpacing: 2 },
  footerDot: { color: C.border, fontSize: s(13) },
});
